import React from "react";
import "./EditUser.scss";
import * as api from "../../shared/api-client";
import * as _ from "lodash";
import { Button, Dialog, DialogContent, TextField, DialogActions, Select, MenuItem, Chip, DialogTitleWithCloseButton, InlineInfoTooltip, InfoAlert, KeyValueEditor, CircularProgress } from "../../shared/components";
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { connect } from "react-redux";
import { showSuccess, showError } from "../../store/notifications/actions";

const schema = z.object({
    name: z.string({ required_error: "Please enter a name for the user." }).min(1, { message: "Please enter a name for the user." }),
    emailAddress: z.string({ required_error: "Please enter an email address for the user." }).includes("@", { message: "Please enter a valid email address." }),
    roleIds: z.array(z.any()),
    metadataEntries: z.array(z.object({
        field: z.string(),
        value: z.string()
    }))
});
type FormValues = z.infer<typeof schema>;

interface EditUserProps {
    open: boolean,
    setOpen: (open: boolean) => void,
    user?: api.UserDisplayModel,
    showSuccess: typeof showSuccess,
    showError: typeof showError,
    onSuccess: () => void
}

function EditUser(props: EditUserProps) {
    const { open, setOpen, user, showSuccess, showError, onSuccess } = props;
    const { handleSubmit, reset, setError, control } = useForm({ resolver: zodResolver(schema), defaultValues: { roleIds: [], metadataEntries: [] } });
    const [rolesLookup, setRolesLookup] = React.useState([]);
    const [formErrors, setFormErrors] = React.useState(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        new api.LookupsClient().getRoles()
            .then(result => setRolesLookup(result));
    }, []);

    React.useEffect(() => {
        reset(user ? { ...user, roleIds: user?.roles?.map(r => r.id) ?? [] } : {});
    }, [user]);

    const closeDialog = () => {
        setOpen(false);
    }

    const attachFormErrors = (errorDictionary: object) => {
        _.forOwn(errorDictionary, (value, key) => {
            if (key === "non_field_errors") {
                setFormErrors(value[0]);
            }
            else {
                setError(key as any, { type: "server", message: value[0] });
            }
        });
    }

    const onSubmit: SubmitHandler<FormValues> = formModel => {
        setSaving(true);
        const model = {
            ...formModel
        };

        // Bulk create...
        if (!user && (formModel.name.includes(",") || formModel.emailAddress.includes(","))) {
            let names = formModel.name.split(",").map(a => a.trim()) as string[];
            let emailAddresses = formModel.emailAddress.split(",").map(a => a.trim()) as string[];

            if (names.length !== emailAddresses.length) {
                setError("name", { type: "validation", message: "Length of names and email addresses do not match.  Please double-check both." });
                setSaving(false);
                return;
            }

            let apiCalls = _.zip(names, emailAddresses).map(async ([name, emailAddress], index) => {
                return await new api.UsersClient()
                    .create({
                        name,
                        emailAddress,
                        roleIds: formModel.roleIds,
                        metadataEntries: formModel.metadataEntries
                    })
                    .then(() => showSuccess(`${name} has been created successfully.`))
                    .catch((error: api.ApiException) => showError(`${name} was not created successfully - ${error.response}`));
            });

            Promise.all(apiCalls).then(() => {
                closeDialog();
                onSuccess();
                setSaving(false);
            });

            return;
        }

        const apiCall = user ?
            new api.UsersClient().update(user.id, model) :
            new api.UsersClient().create(model);

        (apiCall as Promise<any>)
            .then(() => {
                closeDialog();
                showSuccess(`User has been ${user ? "updated" : "created"} successfully.`);
                onSuccess();
            })
            .catch((error: api.ApiException) => {
                if (error.status === 400) {
                    attachFormErrors(JSON.parse(error.response || "{}"));
                }
                else {
                    showError(error.response);
                }
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const getRole = (id) => {
        return _.find(rolesLookup, r => r.id === id);
    }

    return (
        <Dialog
            id="edit-user-dialog"
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick') {
                    closeDialog();
                }
            }}
            aria-labelledby="form-dialog-title">
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitleWithCloseButton onClose={closeDialog} id="form-dialog-title">
                    {`${user ? "Edit " + user.name : "Create User"}`}
                </DialogTitleWithCloseButton>
                <DialogContent className="vertical-form">
                    {
                        !user &&
                        <InfoAlert className="margin-bottom-2">
                            A welcome email will be sent to this user, containing instructions for login/registration.
                        </InfoAlert>
                    }
                    <div className="vertical-form-field">Name</div>
                    <Controller
                        name="name"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                                autoFocus
                            />
                        )}
                    />

                    <div className="vertical-form-field">Email Address</div>
                    <Controller
                        name="emailAddress"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <div className="vertical-form-field">Roles</div>
                    <Controller
                        name="roleIds"
                        control={control}
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <Select
                                multiple
                                error={!!error}
                                value={value}
                                onChange={(event) => onChange(event.target.value as string[])}
                                variant="outlined"
                                fullWidth
                                inputProps={{
                                    id: 'roles',
                                }}
                                renderValue={(selected) => (
                                    <div>
                                        {(selected as string[]).map((value) => (
                                            <Chip key={value} label={getRole(value)?.name} color="primary" className="multiselect-chip" />
                                        ))}
                                    </div>
                                )}
                            >
                                {rolesLookup.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <div className="vertical-form-field">Metadata
                        <InlineInfoTooltip
                            text="Any additional information about the user can be captured here.  This will be available to the application and can be utilized for custom logic/bindings in storylines."
                        />
                    </div>
                    <Controller
                        name="metadataEntries"
                        control={control}
                        render={({
                            field: { value, onChange },
                        }) => (
                            <KeyValueEditor
                                keyName="field"
                                updateEntries={onChange}
                                entries={value}
                            />
                        )}
                    />
                    {
                        formErrors &&
                        <div className="form-errors">
                            {formErrors}
                        </div>
                    }
                </DialogContent>
                <DialogActions>
                    <Button type="submit" variant="contained" color="primary" disabled={saving} startIcon={ saving && <CircularProgress size="small" color="success" style={{ width: 16, height: 16 }} />}>
                        {`${user?.id ? "Update" : "Create"} User`}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default connect(
    null,
    { showSuccess, showError })(EditUser);